import { useContext, useState, useEffect } from "react";

import { Box, MenuItem, TextField } from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';

import SpotReviewContext from "../../../context/spot/spotReviewContext";
import AlertContext from "../../../context/alert/alertContext";
import { useHistory } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';

const WriteContentCreationStep = ({ spotReview, setSpotReview, files, setFiles, previewUrls, placeName, placeAddress, backButton, isDraft, onClose, onDraftSubmit, onSaved, onPreview, lite, isEdit, onEditSubmit, allowFutureDates }) => {
    const [isCreatingSpotReview, setIsCreatingSpotReview] = useState(false);
    const [isTitleMissing, setIsTitleMissing] = useState(false);
    const history = useHistory();

    const spotReviewContext = useContext(SpotReviewContext);
    const {
        addSpotReview,
        addSpotReviewComplete,
        addDraftSpotReview,
        spotReviewAdded,
        spotReviews,
    } = spotReviewContext;

    const alertContext = useContext(AlertContext);
    const { setAlert } = alertContext;

    // Post-save handling. Three branches:
    //   (1) onSaved callback → (legacy — no longer wired from the composer
    //       now that Preview defers POST until Done) hand the saved review
    //       back without navigating. Kept for backwards compat.
    //   (2) Sub-review creation (spotReview.parentId set) → we're attaching
    //       to an existing parent; caller page (detail / preview step)
    //       already listens on spotReviewAdded and refreshes its list.
    //       Just close the modal — navigating to /spots would yank the
    //       user off whichever page triggered the add.
    //   (3) Standalone top-level review (legacy path) → navigate to /spots
    //       as before.
    useEffect(() => {
        if (isCreatingSpotReview && spotReviewAdded) {
            setIsCreatingSpotReview(false);
            setAlert("Successfully posted", "success");
            addSpotReviewComplete();
            if (onSaved) {
                // spotReviews is prepended on ADD_SPOT_REVIEW, so [0] is the
                // newly saved review.
                onSaved(spotReviews && spotReviews[0]);
            } else if (spotReview.parentId && spotReview.relation) {
                if (onClose) onClose();
            } else {
                history.push("/spots");
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCreatingSpotReview, spotReviewAdded, addSpotReviewComplete]);

    const onSubmit = (e) => {
        if (spotReview.title === "") {
            setIsTitleMissing(true);
            setAlert("Please write a title", "error");
            return;
        }
        e.preventDefault();
        // In edit mode with a preview step, go to preview first (onPreview
        // is set). Only call onEditSubmit directly if there's no preview step.
        if (isEdit && onEditSubmit && !onPreview) {
            setIsCreatingSpotReview(true);
            onEditSubmit().finally(() => setIsCreatingSpotReview(false));
            return;
        }
        setIsCreatingSpotReview(true);
        // Caller-owned draft flow (e.g. sub-review being attached to a parent
        // draft in the trip builder). We hand the composed review back rather
        // than dispatching anywhere — the caller decides where it belongs.
        // Carry the resolved place name on the draft. Server responses use
        // `spot_name`; we write both so downstream card renderers can read
        // either shape without caring whether the review came from a draft
        // or the API.
        const draftCommon = {
            ...spotReview,
            tripOnly: isDraft || false,
            placeName: placeName || "",
            spot_name: placeName || "",
            // Carry the formatted address too (both shapes) so trip-preview
            // rows can render it + a Google Maps link before the draft is
            // saved — after save the server populates spot_address.
            placeAddress: placeAddress || "",
            spot_address: placeAddress || "",
            // Google place id, in server shape so SpotAddressLine picks it
            // up from the draft to build a stable Maps link.
            spot_place_id: spotReview.placeId || "",
            photos: (files || []).filter(Boolean).map((file, i) => ({
                image_link: (previewUrls && previewUrls[i]) || URL.createObjectURL(file),
            })),
            files: files || [],
        };
        // Preview-before-POST flow: composer wants the draft so it can render
        // SpotReviewPreview. Nothing hits the BE until the user clicks Done
        // on the preview step — handled by the composer, not here.
        if (onPreview) {
            onPreview(draftCommon);
            setIsCreatingSpotReview(false);
        } else if (onDraftSubmit) {
            onDraftSubmit(draftCommon);
            setIsCreatingSpotReview(false);
            if (onClose) onClose();
        } else if (isDraft) {
            addDraftSpotReview(draftCommon);
            setIsCreatingSpotReview(false);
            if (onClose) onClose();
        } else {
            addSpotReview(spotReview);
            setFiles([]);
        }
        // ref.current.value = "";
    };

    return (
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1, width: '100%', flex: 1 }}>
            <Box sx={{ mb: 1 }}>
                <Box component="h3" sx={{ m: 0, fontSize: '1.25rem', fontWeight: 600, color: 'text.primary' }}>
                    Write something
                </Box>
                <Box sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.5 }}>
                    Give your review a title and share your experience.
                </Box>
            </Box>
            <TextField
                fullWidth
                id="review-title"
                label="Title"
                name="title"
                variant="outlined"
                placeholder="e.g. A hidden gem in the mountains"
                onChange={(e) => {
                    setIsTitleMissing(false);
                    setSpotReview({
                        ...spotReview,
                        [e.target.name]: e.target.value,
                    });
                }}
                value={spotReview.title ?? ""}
                error={isTitleMissing}
                required
            />
            <TextField
                fullWidth
                multiline
                rows={6}
                id="review-description"
                label="Your review"
                name="content"
                placeholder="What was your experience like? Any tips for other visitors?"
                variant="outlined"
                onChange={(e) => {
                    setSpotReview({
                        ...spotReview,
                        [e.target.name]: e.target.value,
                    });
                }}
                value={spotReview.content ?? ""}
            />
            {/* Sub-review (lite) flow skips visit time + duration — those
                only make sense for the main attraction. Price stays because
                it's useful even for a lite entry (parking fees, etc.). */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {!lite && (
                    <TextField
                        id="visit-start-date"
                        label="Visit date"
                        name="visitStartDate"
                        type="date"
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => {
                            const datePart = e.target.value;
                            if (!datePart) {
                                setSpotReview({ ...spotReview, visitStartTime: "" });
                                return;
                            }
                            if (!allowFutureDates) {
                                const now = new Date();
                                const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                                if (datePart > todayStr) {
                                    setAlert("Visit date cannot be in the future", "error");
                                    return;
                                }
                            }
                            const timePart = (spotReview.visitStartTime || '').split('T')[1] || '00:00';
                            setSpotReview({
                                ...spotReview,
                                visitStartTime: `${datePart}T${timePart}`,
                            });
                        }}
                        value={(spotReview.visitStartTime || '').split('T')[0] || ''}
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' } }}
                    />
                )}
                {!lite && (
                    <TextField
                        id="visit-start-time"
                        label="Start time"
                        select
                        variant="outlined"
                        size="small"
                        onChange={(e) => {
                            const datePart = (spotReview.visitStartTime || '').split('T')[0] || new Date().toISOString().split('T')[0];
                            setSpotReview({
                                ...spotReview,
                                visitStartTime: `${datePart}T${e.target.value}`,
                            });
                        }}
                        value={(spotReview.visitStartTime || '').split('T')[1] || ''}
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 120px' } }}
                    >
                        {Array.from({ length: 48 }, (_, i) => {
                            const h = String(Math.floor(i / 2)).padStart(2, '0');
                            const m = i % 2 === 0 ? '00' : '30';
                            const label = `${h}:${m}`;
                            const selectedDate = (spotReview.visitStartTime || '').split('T')[0];
                            const now = new Date();
                            const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                            const isToday = selectedDate === todayStr;
                            const nowMins = now.getHours() * 60 + now.getMinutes();
                            const slotMins = Math.floor(i / 2) * 60 + (i % 2 === 0 ? 0 : 30);
                            const isFuture = !allowFutureDates && isToday && slotMins > nowMins;
                            return <MenuItem key={label} value={label} disabled={isFuture}>{label}</MenuItem>;
                        })}
                    </TextField>
                )}
                {!lite && (
                    <TextField
                        id="visit-duration"
                        label="Visit duration"
                        name="visitDuration"
                        select
                        variant="outlined"
                        size="small"
                        onChange={(e) => {
                            setSpotReview({
                                ...spotReview,
                                visitDuration: e.target.value,
                            });
                        }}
                        value={spotReview.visitDuration ?? ""}
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 140px' } }}
                    >
                        <MenuItem value="30">30 minutes</MenuItem>
                        <MenuItem value="60">1 hour</MenuItem>
                        <MenuItem value="90">1.5 hours</MenuItem>
                        <MenuItem value="120">2 hours</MenuItem>
                        <MenuItem value="180">3 hours</MenuItem>
                        <MenuItem value="240">4 hours</MenuItem>
                        <MenuItem value="360">6 hours</MenuItem>
                        <MenuItem value="480">8 hours</MenuItem>
                        <MenuItem value="720">12 hours</MenuItem>
                        <MenuItem value="1440">Full day</MenuItem>
                    </TextField>
                )}
                <TextField
                    id="review-price"
                    label="Price per person"
                    name="price"
                    type="number"
                    variant="outlined"
                    size="small"
                    placeholder="e.g. 25"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: <Box component="span" sx={{ mr: 0.5, color: 'text.secondary' }}>$</Box>,
                    }}
                    onChange={(e) => {
                        setSpotReview({
                            ...spotReview,
                            price: e.target.value,
                        });
                    }}
                    value={spotReview.price ?? ""}
                    sx={{ flex: { xs: '1 1 100%', sm: '1 1 120px' } }}
                />
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                {backButton}
                <LoadingButton
                    variant="contained"
                    type="submit"
                    color="primary"
                    form="post-composer"
                    onClick={onSubmit}
                    endIcon={onPreview ? null : isEdit ? null : <SendIcon />}
                    loading={isCreatingSpotReview}
                    loadingPosition="end"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
                >
                    {onPreview ? 'Next' : isEdit ? 'Save' : 'Submit'}
                </LoadingButton>
            </Box>
        </Box>
    );
}

export default WriteContentCreationStep;